import type React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import type {SxProps, Theme} from "@mui/material/styles";
import {colors} from "../../theme/tokens";

type SectionCardProps = {
    title?: React.ReactNode;
    helper?: React.ReactNode;
    actions?: React.ReactNode;
    /** Step number shown in a navy badge (Create and Edit tenant sections). */
    number?: number;
    id?: string;
    /** Content spans the card edge to edge, e.g. a table. */
    flush?: boolean;
    children?: React.ReactNode;
    sx?: SxProps<Theme>;
    headingLevel?: "h2" | "h3";
    /** The body is collapsed away; the header then closes the card without a divider. */
    collapsed?: boolean;
};

export function SectionCard({title, helper, actions, number, id, flush, children, sx, headingLevel = "h2", collapsed = false}: SectionCardProps) {
    const hasHeader = title !== undefined || actions !== undefined;
    return (
            <Paper
                    variant="outlined"
                    id={id}
                    component="section"
                    aria-labelledby={id && title !== undefined ? `${id}-title` : undefined}
                    sx={{scrollMarginTop: 72, overflow: "hidden", ...sx}}
            >
                {hasHeader && (
                        <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    px: 2,
                                    py: 1.25,
                                    minHeight: 48,
                                    borderBottom: children && !collapsed ? `1px solid ${colors.sectionDivider}` : "none",
                                    flexWrap: "wrap",
                                }}
                        >
                            {number !== undefined && <NumberBadge value={number} />}
                            <Box sx={{flex: 1, minWidth: 0}}>
                                {title !== undefined && (
                                        <Typography
                                                variant={number !== undefined ? "subtitle1" : "h6"}
                                                component={headingLevel}
                                                id={id ? `${id}-title` : undefined}
                                                sx={{color: colors.ink}}
                                        >
                                            {title}
                                        </Typography>
                                )}
                                {helper && (
                                        <Typography variant="body2" component="div" sx={{color: colors.muted}}>
                                            {helper}
                                        </Typography>
                                )}
                            </Box>
                            {actions && <Box sx={{display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap"}}>{actions}</Box>}
                        </Box>
                )}
                {children !== undefined && <Box sx={flush ? undefined : {p: 2}}>{children}</Box>}
            </Paper>
    );
}

function NumberBadge({value}: {value: number}) {
    return (
            <Box
                    aria-hidden
                    sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        backgroundColor: colors.chrome,
                        color: "#fff",
                        fontSize: "11px",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
            >
                {value}
            </Box>
    );
}
